'use client';

import { useCallback, useMemo, type ComponentProps } from 'react';
import {
  TypeformSnippet,
  type TypeformSubmitEvent,
} from 'components/shared/TypeformSnippet';
import { useLegacyCookieAccepted } from 'components/shared/consent/useLegacyCookieAccepted';
import { useConsentRuntime } from 'components/shared/consent/ConsentRuntime';
import { trackPerformanceLabTypeformSubmit } from 'lib/analytics/trackPerformanceLabTypeformSubmit';
import { landingClickIds } from 'lib/analytics/landingParams';

type PerformanceLabTypeformSnippetProps = ComponentProps<typeof TypeformSnippet>;

/** Typeform embed that pushes a dataLayer/gtag event on submit for Google Ads. */
export const PerformanceLabTypeformSnippet = ({
  onSubmit,
  ...props
}: PerformanceLabTypeformSnippetProps) => {
  const handleSubmit = useCallback(
    (event: TypeformSubmitEvent) => {
      trackPerformanceLabTypeformSubmit(event);
      onSubmit?.(event);
    },
    [onSubmit],
  );

  const { stack, granted } = useConsentRuntime();
  const legacyAccepted = useLegacyCookieAccepted();
  const adsAllowed = stack === 'next' ? granted.ads : legacyAccepted;

  const hiddenParams = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    const ids = landingClickIds(window.location.search, adsAllowed);
    return Object.keys(ids).length > 0 ? ids : undefined;
  }, [adsAllowed]);

  return (
    <TypeformSnippet
      {...props}
      hiddenParams={hiddenParams}
      onSubmit={handleSubmit}
    />
  );
};
